"use client";
import {
  Box,
  Button,
  HStack,
  Heading,
  Link,
  Stack,
  Text,
  useToast,
  Select,
} from "@chakra-ui/react";
import { CustomInput } from "../components/Input";
import React, { useState, useEffect } from "react";
import { useApiData } from "../components/GetData";
import { useFormik } from "formik";
import * as Yup from "yup";
import { API_BASE_URL } from "../components/APIs";
import axios from "axios";
import Cookies from "js-cookie";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import { WarningIcon } from "@chakra-ui/icons";
import PhoneNumberInput from "../components/PhoneNumberInput";
import FormNavigation from "../components/FormNavigation";
import { LoadingSpinner } from "../components/LoadingSpinner";

export const Referee = () => {
  const [isLoading, setLoading] = useState(false);
  const [refList, setRefList] = useState([]);
  const [refLoading, setRefLoading] = useState(false);
  const toast = useToast();
  const uid = Cookies.get("uid");

  // Fetch Referee Data
  const { data: refData, loading, fetchData } = useApiData("onboarding/getReferee");
  const { data: offerAcceptance, fetchData: fetchOffer } = useApiData("onboarding/getOfferAccepted");

  useEffect(() => {
    fetchData();
    fetchOffer();
  }, []);

  useEffect(() => {
    if (refData && Array.isArray(refData)) setRefList(refData);
  }, [refData]);

  // Formik setup
  const formik = useFormik({
    initialValues: {
      ref_type: "",
      ref_fname: "",
      ref_lname: "",
      ref_mobile: "",
      ref_email: "",
      user_id: uid,
    },
    validationSchema: Yup.object({
      ref_type: Yup.string().required("Select the type of reference"),
      ref_fname: Yup.string().required("First name is required"),
      ref_lname: Yup.string().required("Last name is required"),
      ref_email: Yup.string().email("Enter a valid email").required("Email is required"),
      ref_mobile: Yup.string().required("Mobile number is required"),
    }),
    onSubmit: async (data, { resetForm }) => {
      try {
        setLoading(true);
        const url = API_BASE_URL + "onboarding/saveReferee";
        const response = await axios.post(url, data);

        if (response.data.status === 200) {
          toast({
            title: "Referee Added",
            description: response.data.message,
            position: "top",
            status: "success",
            isClosable: true,
          });
          resetForm();
          await fetchData(); // Refresh list
        } else {
          toast({
            title: "Error",
            description: response.data.message,
            position: "top",
            status: "error",
            isClosable: true,
          });
        }
      } catch (error) {
        toast({
          title: "Network Error",
          description: error.message,
          position: "top",
          status: "error",
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleDelete = async (id) => {
    try {
      setRefLoading(true);
      const url = API_BASE_URL + "onboarding/deleteReferee";
      const response = await axios.post(url, { referee_id: id });

      if (response.data.status === 200) {
        toast({
          title: "Referee Deleted",
          description: response.data.message,
          position: "top",
          status: "success",
          isClosable: true,
        });
        await fetchData(); // Refresh list after deletion
      } else {
        toast({
          title: "Delete Error",
          description: response.data.message,
          position: "top",
          status: "error",
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while deleting the referee.",
        position: "top",
        status: "error",
        isClosable: true,
      });
    } finally {
      setRefLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <OnboardingLayout
      pageTitle="Reference"
      description="We need at least two references — from previous employers or character references if you have been unemployed or studying."
      mainContent={
        offerAcceptance?.accept === 1 ? (
          <Box as="form" onSubmit={formik.handleSubmit}>
            <Stack spacing={5}>
              <HStack spacing={4} flexWrap="wrap">
                {/* Type of Reference */}
                <Box flex="1">
                  <Text fontWeight={600}>Type of Reference</Text>
                  <Select
                    name="ref_type"
                    placeholder="Select type"
                    value={formik.values.ref_type}
                    onChange={formik.handleChange}
                    isInvalid={formik.touched.ref_type && formik.errors.ref_type}
                  >
                    <option value="Professional">Professional</option>
                    <option value="Academic">Academic</option>
                    <option value="Character">Character</option>
                  </Select>
                  {formik.touched.ref_type && formik.errors.ref_type && (
                    <Text color="red.500" fontSize="sm">
                      {formik.errors.ref_type}
                    </Text>
                  )}
                </Box>

                {/* Name fields */}
                <CustomInput
                  value={formik.values.ref_fname}
                  isInvalid={formik.errors.ref_fname && formik.touched.ref_fname}
                  formErroMsg={formik.errors.ref_fname}
                  onChange={formik.handleChange}
                  formLabel="First Name"
                  type="text"
                  placeholder="Referee's First Name"
                  name="ref_fname"
                />
                <CustomInput
                  value={formik.values.ref_lname}
                  isInvalid={formik.errors.ref_lname && formik.touched.ref_lname}
                  formErroMsg={formik.errors.ref_lname}
                  onChange={formik.handleChange}
                  formLabel="Last Name"
                  type="text"
                  placeholder="Referee's Last Name"
                  name="ref_lname"
                />
              </HStack>

              <HStack spacing={4} flexWrap="wrap">
                <PhoneNumberInput
                  value={formik.values.ref_mobile}
                  isInvalid={formik.errors.ref_mobile && formik.touched.ref_mobile}
                  onChange={(value) => formik.setFieldValue("ref_mobile", value)}
                  formLabel="Referee Phone Number"
                  name="ref_mobile"
                />
                <CustomInput
                  value={formik.values.ref_email}
                  isInvalid={formik.errors.ref_email && formik.touched.ref_email}
                  formErroMsg={formik.errors.ref_email}
                  onChange={formik.handleChange}
                  formLabel="Referee Email Address"
                  type="email"
                  placeholder="Referee Email"
                  name="ref_email"
                />
              </HStack>

              <FormNavigation
                formik={formik}
                isLoading={isLoading}
                setIsLoading={setLoading}
                nextRoute={null}
                previousRoute="/employment-check/dbs"
                progressKey="referee"
              />

              {/* --- Referee List --- */}
              <Box mt={8}>
                <Heading fontSize="lg" mb={4}>
                  Submitted References
                </Heading>
                {refList.length > 0 ? (
                  <Stack spacing={4}>
                    {refList.map((ref, index) => (
                      <Box
                        key={index}
                        bg="green.50"
                        p={4}
                        rounded="md"
                        border="1px solid"
                        borderColor="green.200"
                      >
                        <Text fontWeight={600}>
                          Referee {index + 1}: {ref.ref_fname} {ref.ref_lname}
                        </Text>
                        <Text>
                          <b>Type:</b> {ref.ref_type || "N/A"}
                        </Text>
                        <Text>
                          <b>Email:</b> {ref.ref_email}
                        </Text>
                        <Text>
                          <b>Phone:</b> {ref.ref_mobile}
                        </Text>
                        <Text>
                          <b>Responded:</b>{" "}
                          {ref.isRefResponded === "true" ? "✅ Responded" : "❌ Not Responded"}
                        </Text>
                        <Button
                          mt={3}
                          colorScheme="red"
                          size="sm"
                          onClick={() => handleDelete(ref.referee_id)}
                          isLoading={refLoading}
                        >
                          Delete
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Text color="red.500" fontWeight="medium">
                    No referees added yet.
                  </Text>
                )}
              </Box>
            </Stack>
          </Box>
        ) : (
          <Stack
            width={["100%", "60%"]}
            mx="auto"
            bg="red.50"
            rounded="lg"
            alignItems="center"
            p="2em"
            textAlign="center"
          >
            <WarningIcon fontSize="3em" color="red.500" />
            <Text color="red.600" fontSize="15pt" fontWeight={700}>
              Please accept the provisional offer{" "}
              <Link color="blue.500" href="/employment-check/provisional-offer">
                here
              </Link>{" "}
              before submitting your Proof of Right to Work.
            </Text>
          </Stack>
        )
      }
    />
  );
};
